'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { FilmSources } from '@/components/marketing/FilmSources';
import { DETECTION_START, READY_AT, type ProductFrame } from '@/lib/productFrames';

interface ProductShowcaseProps {
  frame: ProductFrame;
  className?: string;
  float?: boolean;
  priority?: boolean;
  video?: boolean;
  /** Called once when the looping recording wraps back to its start. */
  onPlaythrough?: () => void;
  /** Called when the visitor pauses the video with its own control. */
  onVisitorPause?: () => void;
  /** The recording failed to load or decode; the still is shown instead. */
  onMediaError?: () => void;
  /** Tracks play/pause so a parent timer can re-arm around real playback. */
  onPlayingChange?: (playing: boolean) => void;
  /** Live visibility of this showcase: false pauses the recording (without
   *  unmounting it); true resumes it unless the visitor paused it. Omit it and
   *  the showcase observes its own viewport: the recording mounts on first
   *  sight and pauses whenever it leaves view. */
  autoPlayGate?: boolean;
  /** Each new value asks a paused video to play (e.g. the tour resuming). */
  playRequest?: number;
}

/**
 * Display a product capture, using video when available and motion is allowed.
 *
 * Keep the still visible until playback reaches the frame's ready threshold
 * (28 seconds for the widget recording, 1 second for a choreographed film);
 * media errors restore the still. A recording with a lead-in requests its
 * start (29 seconds) and seeks there whenever playback is before the act — an
 * ignored `#t=` fragment or a loop wrap. A film that starts at 0 is never
 * seeked. Report a wrap at most once per mount, when `onPlaythrough` is
 * supplied.
 *
 * `float` controls idle motion; `priority` loads the still eagerly. A changed,
 * nonzero `playRequest` attempts to resume a paused recording. Rejected play
 * requests leave the control paused, and `onVisitorPause` runs only when the
 * visitor uses the control to pause.
 */
export function ProductShowcase({
  frame,
  className = '',
  float = true,
  priority = false,
  video = true,
  onPlaythrough,
  onVisitorPause,
  onMediaError,
  onPlayingChange,
  autoPlayGate,
  playRequest = 0,
}: ProductShowcaseProps) {
  const reduced = useReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [paused, setPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const playthroughReported = useRef(false);
  // True only when the VISITOR paused via the control. Programmatic pauses
  // (an offscreen autoPlayGate) must not suppress the gate's own resume.
  const visitorPausedRef = useRef(false);
  // Mirror of videoReady the media handlers can read synchronously: within one
  // timeupdate event the state setter has not applied yet, so a loop wrap that
  // arrives in the same tick as readiness would otherwise read the stale value.
  const videoReadyRef = useRef(false);
  // Without a parent gate (feature pages) the showcase gates itself: nothing
  // is fetched before the frame first scrolls into view, and playback pauses
  // offscreen. No IntersectionObserver: behave as always visible.
  const selfGated = autoPlayGate === undefined;
  const rootRef = useRef<HTMLDivElement>(null);
  const [selfVisible, setSelfVisible] = useState(() => typeof IntersectionObserver === 'undefined');
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = rootRef.current;
    if (!selfGated || !el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setSelfVisible(entry.isIntersecting);
        if (entry.isIntersecting) setSeen(true);
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [selfGated]);
  const playGate = autoPlayGate ?? selfVisible;
  const tryVideo =
    video && (!selfGated || seen) && Boolean(frame.video) && !videoFailed && !reduced;
  // Per-frame seek/readiness thresholds: choreographed films open on their
  // act (start 0, ready immediately); the widget recording skips its lead-in.
  const startAt = frame.filmStart ?? DETECTION_START;
  const readyAt = frame.filmReady ?? READY_AT;

  // Looping motion that starts on its own needs a way to stop it (WCAG 2.2.2).
  // The label follows the element's own play/pause events, so a blocked
  // autoplay or a rejected play() never leaves the control saying "Pause" on a
  // stopped video.
  const togglePlayback = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      visitorPausedRef.current = false;
      void el.play().catch(() => setPaused(true));
    } else {
      visitorPausedRef.current = true;
      el.pause();
      onVisitorPause?.();
    }
  };

  // A resumed tour must resume the recording too; otherwise its fallback
  // timer skips a video that stays paused. 0 is "no request yet".
  useEffect(() => {
    const el = videoRef.current;
    if (playRequest === 0 || !el?.paused) return;
    // The tour is resuming on the visitor's behalf: the pause was no longer
    // visitor-owned, so the visibility gate may auto-resume again later.
    visitorPausedRef.current = false;
    void el.play().catch(() => setPaused(true));
  }, [playRequest]);

  // The visibility gate pauses the recording offscreen and resumes it on
  // return — without unmounting the element, so a visitor pause survives
  // scrolling and autoPlay cannot restart it from scratch.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (!playGate) {
      if (!el.paused) el.pause();
      return;
    }
    if (!visitorPausedRef.current && el.paused) {
      void el.play().catch(() => {});
    }
  }, [playGate]);

  // Skip straight to the AI-detection act: the recording's first ~28s are the
  // address form; the footprint sweep (the wow) lands ~30s in, and the loop
  // wraps back to the same point so it replays the detection, not the typing.
  // Every source carries the same #t=startAt fragment (FilmSources).
  // No encode could load or decode: the still takes over for good.
  const onVideoFailed = () => {
    videoReadyRef.current = false;
    setVideoFailed(true);
    setVideoReady(false);
    onMediaError?.();
  };

  // Entrance animation lives on the outer element; the idle float lives on a
  // separate inner element — in Framer Motion 11 whileInView owns `y` after
  // the first viewport entry, so combining both on one element would silence
  // the float.
  return (
    <motion.div
      ref={rootRef}
      initial={reduced ? false : { opacity: 0, y: 28, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`relative ${className}`}
    >
      <motion.div
        animate={reduced || !float ? undefined : { y: [0, -8, 0] }}
        transition={
          reduced || !float ? undefined : { duration: 8, repeat: Infinity, ease: 'easeInOut' }
        }
      >
        <div className="p-[1px] rounded-[2rem] gradient-border shadow-[0_32px_80px_-24px_rgba(0,0,0,0.65)]">
          <div className="rounded-[calc(2rem_-_1px)] overflow-hidden bg-[#0a0a0c]">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0c0c10] border-b border-white/[0.06]">
              <span className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-white/20" />
                <span className="w-2 h-2 rounded-full bg-white/15" />
                <span className="w-2 h-2 rounded-full bg-white/15" />
              </span>
              <span className="ml-2 text-[11px] text-white/40 tracking-wide truncate">
                {frame.illustration
                  ? `Rooflyzer OS · ${frame.label} · Illustration`
                  : `Rooflyzer OS · Summit Ridge Roofing · ${frame.label}`}
              </span>
            </div>
            <div className="relative bg-[#0a0a0c]">
              <img
                src={frame.still}
                alt={frame.alt}
                className={`w-full h-auto block transition-opacity duration-700 ${
                  tryVideo && videoReady ? 'opacity-0' : 'opacity-100'
                }`}
                loading={priority ? 'eager' : 'lazy'}
              />
              {tryVideo ? (
                <video
                  ref={videoRef}
                  className={`w-full h-auto block absolute inset-0 transition-opacity duration-700 ${
                    videoReady ? 'opacity-100' : 'opacity-0'
                  }`}
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster={frame.still}
                  onLoadedData={(event) => {
                    // Readiness is decided only in timeupdate: an empty
                    // seekable range does not stop timeupdate reporting
                    // progress, and marking ready here would crossfade to the
                    // loading lead-in whenever the #t= start is not applied.
                    setPaused(event.currentTarget.paused);
                    onPlayingChange?.(!event.currentTarget.paused);
                  }}
                  onPlay={() => {
                    setPaused(false);
                    onPlayingChange?.(true);
                  }}
                  onTimeUpdate={(event) => {
                    // Readiness lives here, not in a poll: loadeddata fires
                    // before autoplay's play event, so a rAF check sees
                    // paused=true on its first tick and would give up while
                    // the recording plays on, hidden under the still.
                    // timeupdate fires during playback and needs no cleanup.
                    const el = event.currentTarget;
                    if (el.currentTime >= readyAt) {
                      videoReadyRef.current = true;
                      setVideoReady(true);
                      return;
                    }
                    // Before the ready threshold: either the browser ignored the
                    // #t= fragment (not ready yet — keep the still up instead of
                    // revealing the lead-in), or `loop` restarted at 0 and
                    // the fragment only set the FIRST start. Seek to the act —
                    // but only when there is a lead-in to skip. A film whose
                    // start is already below its ready threshold plays into
                    // readiness by itself; seeking it to its start on every
                    // timeupdate would rewind it forever.
                    if (startAt >= readyAt) el.currentTime = startAt;
                    // Count a playthrough only on a real wrap, and only when
                    // someone is listening: a loop that wraps while the tour
                    // is paused must not use up the one report it waits for.
                    if (videoReadyRef.current && onPlaythrough && !playthroughReported.current) {
                      playthroughReported.current = true;
                      onPlaythrough();
                    }
                  }}
                  onPause={() => {
                    setPaused(true);
                    onPlayingChange?.(false);
                  }}
                  onError={(event) => {
                    // React bubbles a <source> error up to here; a source
                    // failing is not the film failing (FilmSources decides).
                    if (event.target === event.currentTarget) onVideoFailed();
                  }}
                >
                  <FilmSources frame={frame} startAt={startAt} onAllFailed={onVideoFailed} />
                </video>
              ) : null}
              <div className="pointer-events-none absolute inset-0 product-shine" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              {/* Rendered whenever a video is attempted, not only once it is
                  ready: a blocked autoplay never reaches timeupdate, so a
                  readiness gate would hide the only way to start it. */}
              {tryVideo ? (
                <button
                  type="button"
                  onClick={togglePlayback}
                  aria-label={paused ? 'Play product video' : 'Pause product video'}
                  className="absolute bottom-3 right-3 z-10 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full bg-black/60 px-3 text-xs font-medium text-white/90 backdrop-blur hover:bg-black/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                >
                  {paused ? 'Play' : 'Pause'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
